import { Page } from '$core/models/page';
import { Location } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  Resolve,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { BehaviorSubject, interval, Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  first,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { ErrorsHandlerService } from './errors-handler.service';

@Injectable({
  providedIn: 'root',
})
export class PagesService {
  public page$ = new BehaviorSubject<Page>(null);
  private _page: Page;
  public get page(): Page {
    return this._page;
  }

  public set page(value: Page) {
    this._page = value;
    this.page$.next(this._page);
  }

  constructor(private http: HttpClient) {}

  public get(id: number | string) {
    return this.http
      .get<Page>(id ? `/v1/pages/${id}` : '/v1/pages')
      .pipe(map((res) => new Page(res)));
  }

  public hasPublications(): Observable<boolean> {
    return this.http
      .get<{ has_publications: boolean }>(`/v1/has-available-publications`)
      .pipe(
        map((res) => res.has_publications),
        filter<boolean>(Boolean)
      );
  }
}

@Injectable({
  providedIn: 'root',
})
export class PageResolver implements Resolve<Observable<Page>> {
  constructor(
    private pageService: PagesService,
    private errorsHandlerService: ErrorsHandlerService,
    private location: Location,
    private router: Router
  ) {}

  public resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const pageID = route.paramMap.get('id') || route.data.pageID;

    // Zero interval prevents double request when navigate on dymamic route
    return interval().pipe(
      first(),
      switchMap(() =>
        this.pageService.get(pageID).pipe(
          tap((res) => {
            this.pageService.page = res;

            if (res.external_url) {
              location.replace(res.external_url);
            }
          }),
          catchError((error: HttpErrorResponse) => {
            if (error instanceof HttpErrorResponse) {
              const url = state.url;
              this.errorsHandlerService.handleErrorCode(error);

              this.router.events
                .pipe(
                  filter((event) => event instanceof NavigationEnd),
                  take(1)
                )
                .subscribe(() => this.location.replaceState(url));
            } else {
              throw error;
            }

            return of(null);
          })
        )
      )
    );
  }
}
